import React from 'react';
import { Calendar, Mail, MapPin, Phone, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  new: 'bg-yellow-100 text-yellow-800',
  contacted: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-green-600 text-white',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const formatDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const ContactDetails = ({ booking }) => (
  <div className="space-y-2">
    <h4 className="font-serif text-lg text-[#8B7355] mb-3">Contact</h4>
    <p className="text-[#8B7355]/80 flex items-center gap-2"><User className="w-4 h-4" /> {booking.name}</p>
    <p className="text-[#8B7355]/80 flex items-center gap-2">
      <Mail className="w-4 h-4" />
      <a href={`mailto:${booking.email}`} className="hover:text-[#C9A961]">{booking.email}</a>
    </p>
    <p className="text-[#8B7355]/80 flex items-center gap-2">
      <Phone className="w-4 h-4" />
      <a href={`tel:${booking.phone}`} className="hover:text-[#C9A961] font-semibold">{booking.phone}</a>
    </p>
    <p className="text-[#8B7355]/80 flex items-center gap-2"><Calendar className="w-4 h-4" /> {booking.event_date}</p>
    <p className="text-[#8B7355]/80 flex items-center gap-2"><MapPin className="w-4 h-4" /> {booking.event_location}</p>
    {booking.guest_count && <p className="text-[#8B7355]/80 text-sm">Guests: {booking.guest_count}</p>}
  </div>
);

const QuoteSummary = ({ booking }) => {
  const { quote } = booking;
  return (
    <div className="space-y-3">
      <h4 className="font-serif text-lg text-[#8B7355] mb-3">Quote Details</h4>
      <div className="bg-[#F5F1E8] p-4 rounded-lg space-y-2">
        <div className="flex justify-between">
          <span className="text-[#8B7355]/70">Package:</span>
          <span className="font-semibold text-[#8B7355]">{quote.package_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#8B7355]/70">Package Price:</span>
          <span className="text-[#8B7355]">${quote.package_price}</span>
        </div>
        {quote.addons && quote.addons.length > 0 && (
          <div>
            <p className="text-[#8B7355]/70 mb-1">Add-ons:</p>
            {quote.addons.map((addon) => (
              <div key={`${booking.id}-${addon.name}`} className="flex justify-between text-sm ml-4">
                <span className="text-[#8B7355]/70">{addon.name}</span>
                <span className="text-[#8B7355]">${addon.price}</span>
              </div>
            ))}
          </div>
        )}
        {quote.travel_fee > 0 && (
          <div className="flex justify-between">
            <span className="text-[#8B7355]/70">Travel Fee ({quote.distance_km}km):</span>
            <span className="text-[#8B7355]">${quote.travel_fee}</span>
          </div>
        )}
        <div className="border-t border-[#8B7355]/20 pt-2 mt-2">
          <div className="flex justify-between font-bold">
            <span className="text-[#8B7355]">Total to quote:</span>
            <span className="text-[#C9A961] text-lg">${quote.total_amount}</span>
          </div>
          <p className="text-xs text-[#8B7355]/60 mt-2 italic">
            Payment arranged manually — call the customer to confirm details.
          </p>
        </div>
      </div>
    </div>
  );
};

export const BookingCard = ({ booking }) => {
  const statusClass = STATUS_COLORS[booking.status] || 'bg-gray-100 text-gray-800';
  return (
    <Card className="border-2 border-[#8B7355]/20" data-testid={`booking-card-${booking.id}`}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="font-serif text-2xl text-[#8B7355] mb-2">{booking.name}</CardTitle>
            <Badge className={statusClass}>{booking.status.replace(/_/g, ' ').toUpperCase()}</Badge>
          </div>
          <p className="text-sm text-[#8B7355]/60">{formatDate(booking.created_at)}</p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <ContactDetails booking={booking} />
          {booking.quote && <QuoteSummary booking={booking} />}
        </div>
        {booking.additional_notes && (
          <div className="mt-4 pt-4 border-t border-[#8B7355]/20">
            <h4 className="font-serif text-sm text-[#8B7355] mb-2">Additional Notes:</h4>
            <p className="text-[#8B7355]/70 text-sm">{booking.additional_notes}</p>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-[#8B7355]/20">
          {booking.media_consent ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">
                Media Consent Granted
              </span>
              <span className="text-[#8B7355]/70">- OK to post photos/videos on social media</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">
                No Media Consent
              </span>
              <span className="text-[#8B7355]/70">- Do not post photos/videos from this event</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
